/**
 * Conexiunea Socket.IO (spec §5). Cameră per utilizator pe server; ascultăm
 * `clip.updated` + `notification.new` + evenimente generice (progres import).
 * La reconectare: refetch pentru clipuri/notificări (resync stare). Cookie de sesiune
 * e first-party prin proxy-ul Vite (`/socket.io`).
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '../api/hooks';

export interface ClipUpdatePayload {
  clipId: string;
  status: string;
  pct: number;
  step?: string | null;
  index?: number;
  total?: number;
  costTotalCents?: number;
  error?: string | null;
}

export interface NotificationPayload {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  createdAt: string;
  unread: number;
}

type ClipListener = (p: ClipUpdatePayload) => void;
type NotifListener = (p: NotificationPayload) => void;
type GenericListener = (event: string, payload: unknown) => void;

interface RealtimeCtx {
  connected: boolean;
  onClipUpdate: (fn: ClipListener) => () => void;
  onNotification: (fn: NotifListener) => () => void;
  onEvent: (fn: GenericListener) => () => void;
}

const Ctx = createContext<RealtimeCtx | null>(null);

export function RealtimeProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const clipListeners = useRef(new Set<ClipListener>());
  const notifListeners = useRef(new Set<NotifListener>());
  const genericListeners = useRef(new Set<GenericListener>());

  useEffect(() => {
    if (!enabled) return;
    const base = (import.meta.env.VITE_API_BASE as string | undefined) || '';
    const socket = base
      ? io(base, { withCredentials: true, transports: ['websocket', 'polling'] })
      : io({ path: '/socket.io', withCredentials: true, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('connected', () => setConnected(true));
    socket.io.on('reconnect', () => {
      // Resync: reîmprospătează clipuri + notificări.
      qc.invalidateQueries({ queryKey: ['clip'] });
      qc.invalidateQueries({ queryKey: ['clips'] });
      qc.invalidateQueries({ queryKey: qk.notifications });
    });
    socket.on('disconnect', () => setConnected(false));

    socket.on('clip.updated', (payload: ClipUpdatePayload) => {
      clipListeners.current.forEach((fn) => fn(payload));
      qc.invalidateQueries({ queryKey: qk.clip(payload.clipId) });
      qc.invalidateQueries({ queryKey: ['clips'] });
    });
    socket.on('notification.new', (payload: NotificationPayload) => {
      notifListeners.current.forEach((fn) => fn(payload));
      qc.invalidateQueries({ queryKey: qk.notifications });
    });
    // Evenimente generice (ex. progres import produs).
    socket.onAny((event: string, payload: unknown) => {
      if (event === 'clip.updated' || event === 'notification.new') return;
      genericListeners.current.forEach((fn) => fn(event, payload));
    });

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [enabled, qc]);

  const value = useMemo<RealtimeCtx>(
    () => ({
      connected,
      onClipUpdate: (fn) => {
        clipListeners.current.add(fn);
        return () => clipListeners.current.delete(fn);
      },
      onNotification: (fn) => {
        notifListeners.current.add(fn);
        return () => notifListeners.current.delete(fn);
      },
      onEvent: (fn) => {
        genericListeners.current.add(fn);
        return () => genericListeners.current.delete(fn);
      },
    }),
    [connected],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRealtime(): RealtimeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return {
      connected: false,
      onClipUpdate: () => () => undefined,
      onNotification: () => () => undefined,
      onEvent: () => () => undefined,
    };
  }
  return ctx;
}
